
import React, { useState, useCallback, useEffect } from 'react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import type { CarouselApi } from '@/components/ui/carousel';
import type { Screenshot } from './types';
import ScreenshotCard from './ScreenshotCard';
import NavigationIndicators from './NavigationIndicators';

interface DesktopScreenshotCarouselProps {
  screenshots: Screenshot[];
}

const DesktopScreenshotCarousel: React.FC<DesktopScreenshotCarouselProps> = ({ screenshots }) => {
  const [api, setApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const screenshotsPerSlide = 3;
  const totalSlides = Math.ceil(screenshots.length / screenshotsPerSlide);

  const onSelect = useCallback((emblaApi: CarouselApi) => {
    if (!emblaApi) return;
    setCurrentSlide(emblaApi.selectedScrollSnap());
  }, []);

  const handleSlideClick = useCallback((index: number) => {
    if (!api) return;
    api.scrollTo(index);
  }, [api]);

  useEffect(() => {
    if (!api) return;
    
    onSelect(api);
    api.on("select", onSelect);
    
    return () => {
      api.off("select", onSelect);
    };
  }, [api, onSelect]);

  return (
    <div className="hidden lg:block">
      <Carousel 
        className="w-full" 
        setApi={setApi}
        opts={{
          align: "start",
          loop: false,
          dragFree: true,
        }}
      >
        <CarouselContent>
          {Array.from({ length: totalSlides }).map((_, slideIndex) => {
            const startIndex = slideIndex * screenshotsPerSlide;
            const endIndex = Math.min(startIndex + screenshotsPerSlide, screenshots.length);
            const slideScreenshots = screenshots.slice(startIndex, endIndex);
            
            return (
              <CarouselItem key={slideIndex}>
                <div className="grid grid-cols-3 gap-6 lg:gap-8">
                  {slideScreenshots.map((screenshot, index) => (
                    <ScreenshotCard 
                      key={startIndex + index} 
                      screenshot={screenshot} 
                      isMobile={false}
                    />
                  ))}
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        
        <CarouselPrevious />
        <CarouselNext />
      </Carousel>
      
      <NavigationIndicators 
        totalSlides={totalSlides} 
        currentSlide={currentSlide}
        onSlideClick={handleSlideClick}
      />
    </div>
  );
};

export default DesktopScreenshotCarousel;
